"use client";

import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import React from "react";
import api from "@/lib/axiosInstance";
import Image from "next/image";

// Constants
const BLOGS_TO_DISPLAY = 4;
const QUERY_STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  featuredImage: string;
  featured: boolean;
  published: boolean;
  publishedAt: string;
  readTime: number;
  author: {
    name: string;
    avatar?: string;
    role?: string;
  };
  views: number;
  likes: number;
  createdAt: string;
  updatedAt: string;
}

// Extract API call outside component
async function fetchBlogs(): Promise<BlogPost[]> {
  const { data } = await api.get<{ success: boolean; blogs: BlogPost[] }>(
    "/blog?limit=4&published=true"
  );
  return data.blogs || [];
}

// Error component
function ErrorState({ error }: { error: Error }) {
  return (
    <div className="text-center py-12">
      <div className="text-red-500 mb-4">
        <svg
          className="w-12 h-12 mx-auto"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold mb-2">Unable to load blogs</h3>
      <p className="text-gray-600 text-sm">Please try again later</p>
    </div>
  );
}

// Empty state component
function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="text-gray-400 mb-4">
        <svg
          className="w-16 h-16 mx-auto"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold mb-2">No blogs available</h3>
      <p className="text-gray-600">Check back later for new articles</p>
    </div>
  );
}

// Blog Card Component
function BlogCard({ blog }: { blog: BlogPost }) {
  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "spiritual":
        return "bg-green-100 text-green-800";
      case "technical":
        return "bg-blue-100 text-blue-800";
      case "career":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case "spiritual":
        return "Spiritual Growth";
      case "technical":
        return "Technical Insights";
      case "career":
        return "Career Development";
      default:
        return "General";
    }
  };

  return (
    <Link
      href={`/blog/${blog.slug}`}
      className="bg-accent-200 rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
    >
      <div className="h-48 overflow-hidden relative">
        <Image
          src={blog.featuredImage || "/images/placeholder-blog.jpg"}
          alt={blog.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
        />
        {blog.featured && (
          <div className="absolute top-2 right-2 bg-primary-600 text-accent-200 px-2 py-1 rounded text-xs font-semibold">
            Featured
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center mb-3 gap-2 flex-wrap">
          <span
            className={`${getCategoryColor(
              blog.category
            )} text-xs px-2 py-1 rounded-full font-medium`}
          >
            {getCategoryName(blog.category)}
          </span>
          <span className="text-xs text-gray-500">
            {formatDate(blog.publishedAt)}
          </span>
          {blog.readTime && (
            <>
              <span className="text-gray-400">•</span>
              <span className="text-xs text-gray-500">
                {blog.readTime} min read
              </span>
            </>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {blog.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {blog.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {blog.author?.avatar ? (
              <Image
                src={blog.author.avatar}
                alt={blog.author.name}
                width={24}
                height={24}
                className="rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary-200 flex items-center justify-center">
                <span className="text-xs text-primary-700 font-semibold">
                  {blog.author?.name?.charAt(0).toUpperCase() || "A"}
                </span>
              </div>
            )}
            <span className="text-xs text-gray-600">{blog.author?.name}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {blog.views || 0}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Loading skeleton
function BlogGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(BLOGS_TO_DISPLAY)].map((_, i) => (
        <div
          key={i}
          className="bg-accent-200 rounded-lg shadow-sm overflow-hidden animate-pulse"
        >
          <div className="h-48 bg-gray-300"></div>
          <div className="p-6">
            <div className="h-4 bg-gray-300 rounded w-20 mb-3"></div>
            <div className="h-6 bg-gray-300 rounded w-full mb-2"></div>
            <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-2/3"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Blogs() {
  const {
    data: blogs = [],
    error,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["blogs", "home"],
    queryFn: fetchBlogs,
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_STALE_TIME * 2,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Get limited blogs for display
  const displayBlogs = blogs.slice(0, BLOGS_TO_DISPLAY);

  return (
    <section id="blogs" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Description Section */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-primary-100 text-primary-700 font-medium rounded-full text-sm mb-4">
            Latest Articles
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Our <span className="text-primary-700">Blog</span>
          </h2>
          <div className="w-20 h-1 bg-secondary-400 mx-auto mb-8"></div>
          <p className="max-w-3xl mx-auto text-gray-600 text-lg">
            Discover insightful articles, spiritual growth content, technical
            insights, and career development tips from our community.
          </p>
        </div>

        {/* Header Section with Border */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b-2 border-gray-300">
          <h3 className="text-2xl md:text-3xl font-bold">
            <span className="text-primary-700">Blogs</span>
          </h3>
          {!isLoading && !isError && displayBlogs.length > 0 && (
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-accent-200 font-bold rounded-lg hover:bg-primary-700 transition-all duration-200 shadow hover:shadow-lg transform hover:-translate-y-0.5"
            >
              View All
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          )}
        </div>

        {/* Blogs Grid */}
        <div className="min-h-[200px]">
          {isLoading ? (
            <BlogGridSkeleton />
          ) : isError ? (
            <ErrorState error={error as Error} />
          ) : displayBlogs.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayBlogs.map((blog) => (
                <BlogCard key={blog._id} blog={blog} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Blogs;

